import { useRef } from "react";
import { useLanguageContext } from "../../contexts/LanguageContext";
import Button from "../Button";
import FileHelper, { documentExtensions } from "../../helpers/FileHelper";
import { Description, Image, Movie, VolumeUp } from "@mui/icons-material";
import Popover, { IDefaultPopoverProps } from "../Popover";


interface IAttachmentTypeSelectorPopoverProps extends IDefaultPopoverProps {
    acceptCamera?: boolean;
    acceptImage?: boolean;
    acceptVideo?: boolean;
    acceptAudio?: boolean;
    acceptDocument?: boolean;
    capture?: 'environment' | 'user';
    handleUpdateAttachments: (files: File[]) => void;
}

const AttachmentTypeSelectorPopover: React.FunctionComponent<IAttachmentTypeSelectorPopoverProps> = ({
    open,
    handleClose,
    anchorEl,
    acceptImage = false,
    acceptVideo = false,
    acceptAudio = false,
    acceptDocument = false,
    capture = undefined,
    handleUpdateAttachments
}) => {
    const { translate } = useLanguageContext();

    const inputImageRef = useRef<HTMLInputElement | null>(null);
    const inputVideoRef = useRef<HTMLInputElement | null>(null);
    const inputAudioRef = useRef<HTMLInputElement | null>(null);
    const inputDocumentRef = useRef<HTMLInputElement | null>(null);


    return <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        onClose={() => handleClose()}
        className="backdrop-transparent attachmenttypeselector-popover"
        hideBackdrop={false}
    >
        {
            acceptImage && <>
                <input 
                    type='file' 
                    accept="image/*"
                    capture={capture}
                    multiple={true} 
                    ref={inputImageRef} 
                    className='hidden'
                    onChange={e => {
                        const files = Object.values(e.currentTarget.files ?? {}).filter(f => FileHelper.IsImage(f.name));
                        handleUpdateAttachments(files);
                    }}
                />

                <Button
                    label={translate('image')}
                    fullWidth={true}
                    className='text-alwaysshow color-white backgroundcolor-primary'
                    startIcon={<Image />}
                    onClick={() => inputImageRef.current?.click()}
                />
            </>
        }

        {
            acceptVideo && <>
                <input 
                    type='file' 
                    accept="video/*"
                    capture={capture}
                    multiple={true} 
                    ref={inputVideoRef} 
                    className='hidden'
                    onChange={e => {
                        const files = Object.values(e.currentTarget.files ?? {}).filter(f => FileHelper.IsVideo(f.name));
                        handleUpdateAttachments(files);
                    }}
                />

                <Button
                    label={translate('video')}
                    fullWidth={true}
                    className='text-alwaysshow color-white backgroundcolor-secondary'
                    startIcon={<Movie />}
                    onClick={() => inputVideoRef.current?.click()}
                />
            </>
        }

        {
            acceptAudio && <>
                <input 
                    type='file' 
                    accept="audio/*"
                    capture={capture}
                    multiple={true} 
                    ref={inputAudioRef} 
                    className='hidden'
                    onChange={e => {
                        const files = Object.values(e.currentTarget.files ?? {}).filter(f => FileHelper.IsAudio(f.name));
                        handleUpdateAttachments(files);
                    }}
                />

                <Button
                    label={translate('audio')}
                    fullWidth={true}
                    className='text-alwaysshow color-white backgroundcolor-pink'
                    startIcon={<VolumeUp />}
                    onClick={() => inputAudioRef.current?.click()}
                />
            </>
        }

        {
            acceptDocument && <>
                <input 
                    type='file' 
                    accept={documentExtensions.map(e => `.${e}`).join(', ')}
                    multiple={true} 
                    ref={inputDocumentRef} 
                    className='hidden'
                    onChange={e => {
                        const files = Object.values(e.currentTarget.files ?? {});
                        handleUpdateAttachments(files);
                    }}
                />

                <Button
                    label={translate('document')}
                    fullWidth={true}
                    className='text-alwaysshow color-white backgroundcolor-grey-6'
                    startIcon={<Description />}
                    onClick={() => inputDocumentRef.current?.click()}
                />
            </>
        }
    </Popover>;
}

export default AttachmentTypeSelectorPopover;